// ProfileAdminScreen.jsx
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import TextInput from '../../components/FormElements/TextInput';
import PasswordInput from '../../components/FormElements/PasswordInput';
import SelectInput from '../../components/FormElements/SelectInput';
import AlertMessage from '../../components/ui/AlertMessage';
import { useAuth } from '../../contexts/AuthContext';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import defaultUserProfileImage from '../../assets/img/user-profile.jpg'; // Can be a generic or admin-specific default
import PencilFillIcon from '../../components/icons/PencilFillIcon';

const ProfileAdminScreen = () => {
    const { user, token, updateUserInContext } = useAuth();
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [formData, setFormData] = useState({
        name: '', // Admin's name
        email: '',
        // Optional fields for admin, can be removed if not applicable
        birthDate: '',
        country: '',
        newPassword: '',
        repeatPassword: '',
        profileImageFile: null,
        profileImagePreview: defaultUserProfileImage,
    });

    const [message, setMessage] = useState({ type: '', text: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Country options, can be adjusted or removed if not needed for admin
    const countryOptions = [
        { value: "US", label: "Estados Unidos" },
        { value: "MX", label: "México" },
        { value: "ES", label: "España" },
        { value: "CL", label: "Chile" },
        { value: "CO", label: "Colombia" },
    ];

    useEffect(() => {
        if (user) {
            let determinedProfileImage = defaultUserProfileImage;
            if (user.avatar_file_id) {
                determinedProfileImage = `${API_BASE_URL}/files/serve/${user.avatar_file_id}`;
            } else if (user.image && typeof user.image === 'string' && user.image.startsWith('http')) {
                determinedProfileImage = user.image;
            }

            setFormData(prev => ({
                ...prev,
                name: user.name || user.username || '', // Prefer 'name' or 'username'
                email: user.email || '',
                birthDate: user.birth_date ? user.birth_date.split('T')[0] : '',
                country: user.country || '',
                profileImagePreview: determinedProfileImage,
                newPassword: '',
                repeatPassword: '',
                profileImageFile: null,
            }));
        } else {
            // Reset form if user becomes null (e.g., logout)
            setFormData({
                name: '',
                email: '',
                birthDate: '',
                country: '',
                newPassword: '',
                repeatPassword: '',
                profileImageFile: null,
                profileImagePreview: defaultUserProfileImage,
            });
        }
    }, [user, API_BASE_URL]);

    useEffect(() => {
        const currentPreview = formData.profileImagePreview;
        // Clean up blob URL if component unmounts or preview changes
        if (currentPreview && currentPreview.startsWith('blob:')) {
            return () => {
                URL.revokeObjectURL(currentPreview);
            };
        }
    }, [formData.profileImagePreview]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData(prev => ({
                ...prev,
                profileImageFile: file,
                profileImagePreview: URL.createObjectURL(file)
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setIsSubmitting(true);

        if (formData.newPassword && formData.newPassword !== formData.repeatPassword) {
            setMessage({ type: 'danger', text: 'Las nuevas contraseñas no coinciden.' });
            setIsSubmitting(false);
            return;
        }

        if (!user || !user.id) {
            setMessage({ type: 'danger', text: 'Error: No se pudo identificar al administrador.' });
            setIsSubmitting(false);
            return;
        }

        try {
            let newUploadedAvatarFileId = null;

            if (formData.profileImageFile) {
                const imageFormData = new FormData();
                imageFormData.append('avatar', formData.profileImageFile);

                const imageUploadResponse = await fetch(`${API_BASE_URL}/api/files/upload/avatar`, {
                    method: 'POST',
                    body: imageFormData,
                    headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
                });

                if (!imageUploadResponse.ok) {
                    const errorData = await imageUploadResponse.json().catch(() => ({}));
                    throw new Error(errorData.message || `Error al subir la imagen: ${imageUploadResponse.statusText}`);
                }
                const uploadResult = await imageUploadResponse.json();
                newUploadedAvatarFileId = uploadResult.file_id;
            }

            // TODO: Adjust payload and endpoint for admin profile updates
            const adminUpdatePayload = {
                username: formData.name, // Assuming 'name' form field maps to 'username' in backend
                email: user.email, // Email typically not changed here or requires verification
                birth_date: formData.birthDate,
                country: formData.country,
                initial_form_json: user.initial_form_json || null, // Added for consistency
            };

            if (formData.newPassword) {
                adminUpdatePayload.password = formData.newPassword;
            }

            if (newUploadedAvatarFileId) {
                adminUpdatePayload.avatar_file_id = newUploadedAvatarFileId;
            } else if (user.avatar_file_id && !formData.profileImageFile) {
                adminUpdatePayload.avatar_file_id = user.avatar_file_id;
            } else {
                adminUpdatePayload.avatar_file_id = null; // Or omit if API handles absence as "no change"
            }

            // CRITICAL: Change this endpoint to your admin update endpoint
            const adminUpdateResponse = await fetch(`${API_BASE_URL}/api/admins/${user.id}`, { // Example endpoint
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
                body: JSON.stringify(adminUpdatePayload),
            });

            if (!adminUpdateResponse.ok) {
                const errorData = await adminUpdateResponse.json().catch(() => ({}));
                throw new Error(errorData.message || `Error al actualizar el perfil: ${adminUpdateResponse.statusText}`);
            }

            const updatedAdminFromServer = await adminUpdateResponse.json();

            const finalAdminDataForContext = {
                ...user,
                ...updatedAdminFromServer, // Prioritize server response for updated fields
                username: adminUpdatePayload.username || user.username,
                birth_date: adminUpdatePayload.birth_date || user.birth_date,
                country: adminUpdatePayload.country || user.country,
                initial_form_json: adminUpdatePayload.initial_form_json !== undefined ? adminUpdatePayload.initial_form_json : user.initial_form_json,
                avatar_file_id: adminUpdatePayload.avatar_file_id,
            };

            updateUserInContext(finalAdminDataForContext);

            let determinedProfileImage = defaultUserProfileImage;
            if (finalAdminDataForContext.avatar_file_id) {
                determinedProfileImage = `${API_BASE_URL}/files/serve/${finalAdminDataForContext.avatar_file_id}`;
            }

            setFormData(prev => ({
                ...prev,
                name: finalAdminDataForContext.username || '',
                birthDate: finalAdminDataForContext.birth_date ? finalAdminDataForContext.birth_date.split('T')[0] : '',
                country: finalAdminDataForContext.country || '',
                profileImagePreview: determinedProfileImage,
                profileImageFile: null,
                newPassword: '',
                repeatPassword: '',
            }));

            setMessage({ type: 'success', text: 'Perfil de administrador actualizado con éxito.' });

        } catch (error) {
            console.error("Error submitting admin profile:", error);
            setMessage({ type: 'danger', text: error.message || 'Error al guardar los cambios.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <DashboardLayout activeMenuItem="Perfil Administrador" isScrollable={true}>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <h1 className="h2 mb-4">Perfil de Administrador</h1>
            </div>
            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <FormSpinnerOverlay isLoading={isSubmitting} />
                        <div className="card-body">
                            {message.text && <AlertMessage type={message.type} message={message.text} />}
                            <form id="profileAdminForm" onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-3 text-center mb-3 mb-md-0">
                                        <div className="position-relative mx-auto" style={{ width: '150px', height: '150px' }}>
                                            <img
                                                src={formData.profileImagePreview || defaultUserProfileImage}
                                                alt="Avatar"
                                                className="img-fluid rounded-circle shadow-sm"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                            <label htmlFor="profileImageInput" className="btn btn-sm btn-checkin position-absolute p-2 rounded-circle" style={{ bottom: '5px', right: '5px', cursor: 'pointer', lineHeight: 1, boxShadow: '0 0 5px rgba(0,0,0,0.2)' }} title="Editar imagen">
                                                <PencilFillIcon />
                                                <span className="visually-hidden">Editar imagen</span>
                                            </label>
                                            <input type="file" id="profileImageInput" name="profileImage" className="d-none" accept="image/*" onChange={handleImageChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-5 mb-3 mb-md-0 text-start">
                                        <TextInput label="Nombre" id="profileName" name="name" value={formData.name} onChange={handleChange} required />
                                        <TextInput label="Email" id="profileEmail" name="email" type="email" value={formData.email} onChange={handleChange} required disabled /> {/* Email typically not directly editable */}
                                        <TextInput label="Fecha de nacimiento" id="birthDate" name="birthDate" type="date" value={formData.birthDate} onChange={handleChange} iconClass="bi bi-calendar3" />
                                    </div>
                                    <div className="col-md-4 text-start">
                                        <div className="mb-3">
                                            <SelectInput label="País" id="country" name="country" value={formData.country} onChange={handleChange} options={countryOptions} defaultOptionText="Selecciona un país..." />
                                        </div>
                                        <div className="mb-3">
                                            <PasswordInput label="Nueva contraseña" id="newPassword" name="newPassword" value={formData.newPassword} onChange={handleChange} autoComplete="new-password" />
                                        </div>
                                        <div className="mb-3">
                                            <PasswordInput label="Repita la nueva contraseña" id="repeatPassword" name="repeatPassword" value={formData.repeatPassword} onChange={handleChange} autoComplete="new-password" />
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-4">
                                    <div className="col-12 text-end">
                                        <button type="submit" className="btn btn-checkin px-4">Guardar Cambios</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ProfileAdminScreen;